import React from 'react';
import { Calendar, Loader2, AlertCircle, Users, FileText, Vote, CheckCircle, Award, HardHat } from 'lucide-react';
import { calcularDiasRestantes, formatarData, getStatusColor } from '../utils/dateUtils';
import { useCronograma } from '../hooks/useCronograma';
import { useAuth } from '../context/AuthContext';
import { getComissaoVisualInfo, isMiningCompany, getTipoComissao } from '../utils/cipaminUtils';

// Mapeamento de ícones por nome
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar,
  Users,
  FileText,
  Vote,
  CheckCircle,
  Award,
  HardHat,
};

const CronogramaTab: React.FC = () => {
  const { fases, loading, error, dataTerminoMandato, toggleAcao, carregarFases, recriarCronograma } = useCronograma();
  const { empresaSelecionada } = useAuth();
  
  // Detectar tipo de comissão (CIPA ou CIPAMIN)
  // PRIORIDADE: usar campo tipo_comissao do banco
  const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
  
  console.log('🔍 [CronogramaTab] Dados da empresa:', {
    nome: (empresaSelecionada as any)?.nome,
    tipo_comissao: tipoComissaoBanco,
    cnae_principal: cnaeEmpresa,
    empresaCompleta: empresaSelecionada
  });
  
  // Usar tipo_comissao do banco se existir, senão detectar pelo CNAE ou nome da empresa
  const nomeEmpresa = (empresaSelecionada as any)?.nome;
  const isCIPAMIN = tipoComissaoBanco === 'CIPAMIN' || 
                    (!tipoComissaoBanco && isMiningCompany(cnaeEmpresa, nomeEmpresa));
  
  console.log('🔍 [CronogramaTab] Detecção final:', {
    tipoComissaoBanco,
    cnaeEmpresa,
    nomeEmpresa,
    isMiningCompany: isMiningCompany(cnaeEmpresa, nomeEmpresa),
    isCIPAMIN,
    tipoCalculado: getTipoComissao(cnaeEmpresa, nomeEmpresa)
  });
  
  const comissaoInfo = {
    tipo: isCIPAMIN ? 'CIPAMIN' : 'CIPA',
    norma: isCIPAMIN ? 'NR-22' : 'NR-05'
  };
  
  console.log('🔍 Debug Cronograma:', {
    empresaNome: (empresaSelecionada as any)?.nome,
    tipoComissaoBanco,
    cnaeEmpresa,
    isCIPAMIN,
    comissaoInfo
  });

  const isFaseConcluida = (fase: any) => {
    if (!fase.acoes_banco || fase.acoes_banco.length === 0) return false;
    return fase.acoes_banco.every((acao: any) => acao.concluida);
  };
  
  // Mapear artigos NR-05 para NR-22 (CIPAMIN)
  const mapearArtigoParaNR22 = (artigoNR05: string): string => {
    if (!isCIPAMIN) return artigoNR05;
    
    const mapeamento: Record<string, string> = {
      'Art. 5.5.2 NR-05': 'Art. 22.35.2 NR-22',
      'Art. 5.5.1.1 NR-05': 'Art. 22.35.1.1 NR-22',
      'Art. 5.5.3(a) NR-05': 'Art. 22.35.3(a) NR-22',
      'Art. 5.5.3(b) NR-05': 'Art. 22.35.3(b) NR-22',
      'Art. 5.5.3(e) NR-05': 'Art. 22.35.3(e) NR-22',
      'Art. 5.5.3(f) NR-05': 'Art. 22.35.3(f) NR-22',
      'Art. 5.5.3(i) NR-05': 'Art. 22.35.3(i) NR-22',
      'Art. 5.7.1 NR-05': 'Art. 22.36.1 NR-22',
      'Art. 5.4.7 NR-05': 'Art. 22.34.7 NR-22',
    };
    
    return mapeamento[artigoNR05] || artigoNR05.replace('NR-05', 'NR-22');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando cronograma...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Erro ao carregar cronograma: {error}</p>
          <button 
            onClick={carregarFases}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // LOG ADICIONAL PARA DEBUG
  console.log('========================================');
  console.log('🔍 CRONOGRAMA - DETECÇÃO DE TIPO');
  console.log('========================================');
  console.log('Empresa:', (empresaSelecionada as any)?.nome);
  console.log('CNAE:', cnaeEmpresa);
  console.log('tipo_comissao do banco:', tipoComissaoBanco);
  console.log('isCIPAMIN calculado:', isCIPAMIN);
  console.log('Norma que será usada:', comissaoInfo.norma);
  console.log('Tipo que será mostrado:', comissaoInfo.tipo);
  console.log('========================================');

  return (
    <div className="space-y-6">
      <div className={`rounded-lg shadow-lg p-6 ${isCIPAMIN ? 'bg-orange-50' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h2 className={`text-2xl font-bold flex items-center gap-3 ${isCIPAMIN ? 'text-orange-800' : 'text-gray-800'}`}>
              {isCIPAMIN ? <HardHat className="text-orange-600" /> : <Calendar className="text-blue-600" />}
              Cronograma {comissaoInfo.tipo} - {comissaoInfo.norma} Conforme
            </h2>
            <button
              onClick={() => {
                if (window.confirm(`Deseja recriar o cronograma ${comissaoInfo.tipo}?\n\nIsso irá apagar as fases atuais e criar novas fases baseadas na NR-${isCIPAMIN ? '22' : '05'}.`)) {
                  recriarCronograma();
                }
              }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                isCIPAMIN 
                  ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              title="Recriar cronograma com as fases corretas"
            >
              🔄 Recriar
            </button>
          </div>
          <div className={`${isCIPAMIN ? 'bg-orange-100 border-orange-300' : 'bg-blue-50 border-blue-200'} border rounded-lg p-3`}>
            <p className={`text-sm ${isCIPAMIN ? 'text-orange-800' : 'text-blue-800'}`}>
              <strong>Datas calculadas automaticamente conforme {comissaoInfo.norma}</strong>
            </p>
            <p className={`text-xs mt-1 ${isCIPAMIN ? 'text-orange-700' : 'text-blue-600'}`}>
              Baseado na data de término do mandato: <strong>{dataTerminoMandato ? dataTerminoMandato.split('-').reverse().join('/') : 'Carregando...'}</strong>
            </p>
            <p className={`text-xs mt-1 ${isCIPAMIN ? 'text-orange-600' : 'text-blue-500'}`}>
              ✅ Todos os prazos são recalculados automaticamente quando a data é alterada
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fases.map((fase) => {
            // Buscar o ícone correto baseado no nome armazenado no banco
            const iconName = typeof fase.icon === 'string' ? fase.icon : 'Calendar';
            const IconComponent = iconMap[iconName] || Calendar;
            const diasRestantes = calcularDiasRestantes(fase.data);
            const concluida = isFaseConcluida(fase);
            
            return (
              <div
                key={fase.id}
                className={`bg-white border-2 rounded-lg p-4 transition-all duration-300 hover:shadow-lg border-gray-200 hover:border-gray-300 ${concluida ? 'bg-green-50 border-green-200' : ''}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`${fase.cor} p-2 rounded-lg`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm leading-tight">{fase.titulo}</h3>
                    <p className={`text-xs mt-1 ${isCIPAMIN ? 'text-orange-600' : 'text-gray-500'}`}>
                      {mapearArtigoParaNR22(fase.artigo)}
                    </p>
                    <p className={`text-xs mt-1 font-medium ${isCIPAMIN ? 'text-orange-700' : 'text-blue-600'}`}>
                      {fase.prazoNR}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Data:</span>
                    <span className={`text-sm font-bold ${getStatusColor(fase.data)}`}>
                      {formatarData(fase.data)}
                    </span>
                  </div>
                  
                  {fase.dataFim && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Até:</span>
                      <span className={`text-sm font-bold ${getStatusColor(fase.dataFim)}`}>
                        {formatarData(fase.dataFim)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Status:</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      diasRestantes < 0 
                        ? 'bg-red-100 text-red-800' 
                        : diasRestantes <= 7 
                        ? 'bg-orange-100 text-orange-800'
                        : diasRestantes <= 30
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {diasRestantes < 0 ? 'Vencido' : `${diasRestantes} dias`}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">Ações Obrigatórias:</h4>
                  <div className="space-y-1">
                    {fase.acoes_banco && fase.acoes_banco.map((acao: any, index: number) => (
                      <label key={acao.id} className="flex items-start gap-2 cursor-pointer">
                        <input
                          id={`acao-${acao.id}`}
                          name={`acao-${acao.id}`}
                          type="checkbox"
                          checked={acao.concluida || false}
                          onChange={() => toggleAcao(fase.id, index)}
                          className="mt-1 w-3 h-3 text-green-600 border-green-300 rounded focus:ring-green-500"
                        />
                        <span className={`text-xs ${
                          acao.concluida 
                            ? 'line-through text-gray-500' 
                            : 'text-gray-700'
                        }`}>
                          {acao.descricao}
                          {acao.concluida && acao.data_conclusao && (
                            <span className="text-xs text-green-600 ml-2">
                              ✓ {new Date(acao.data_conclusao).toLocaleDateString()}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CronogramaTab;
